'use client';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { skillsApi } from '@/lib/api';
import { SkillsByCategory } from '@/types';

export default function Skills() {
  const [skills, setSkills] = useState<SkillsByCategory>({});
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<string>('all');

  useEffect(() => {
    skillsApi.getByCategory().then(data => {
      setSkills(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const categories = ['all', ...Object.keys(skills)];
  const displaySkills = activeCategory === 'all'
    ? Object.values(skills).flat()
    : (skills[activeCategory] || []);

  return (
    <section id="skills" className="section" style={{ background: 'var(--bg-surface)' }}>
      <div className="container">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <div className="accent-line" />
          <h2 className="section-title">Skills & Technologies</h2>
          <p className="section-subtitle">What I work with</p>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '2.5rem' }}
        >
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              style={{
                padding: '0.4rem 1rem', borderRadius: '20px', border: '1px solid',
                borderColor: activeCategory === cat ? 'var(--accent)' : 'var(--border)',
                background: activeCategory === cat ? 'var(--accent)' : 'transparent',
                color: activeCategory === cat ? '#FAF0E6' : 'var(--text-muted)',
                cursor: 'pointer', fontFamily: 'Outfit, sans-serif', fontSize: '0.85rem',
                transition: 'all 0.2s ease', textTransform: 'capitalize',
              }}
            >
              {cat}
            </button>
          ))}
        </motion.div>

        {loading ? (
          <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '3rem' }}>Loading skills...</div>
        ) : Object.keys(skills).length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '3rem' }}>No skills added yet. Add them from the Django admin.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            {displaySkills.map((skill, i) => (
              <motion.div key={skill.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.3, delay: i * 0.05 }} className="card">
                <div style={{ fontWeight: 600, fontFamily: 'Outfit, sans-serif', marginBottom: '0.75rem', color: 'var(--text-primary)' }}>{skill.name}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', textTransform: 'capitalize' }}>{skill.category}</span>
                  <span style={{ fontSize: '0.8rem', color: 'var(--accent)', fontWeight: 600 }}>{skill.proficiency}%</span>
                </div>
                <div style={{ height: '6px', background: 'var(--border)', borderRadius: '3px', overflow: 'hidden' }}>
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${skill.proficiency}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: i * 0.05, ease: 'easeOut' }}
                    style={{ height: '100%', background: 'linear-gradient(90deg, var(--accent), var(--accent-hover))', borderRadius: '3px' }}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
